import React from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Row from '../../../core/layout/Row';
import { Roles } from '../../../core/util';
import Async from '../../../core/layout/Async';
import GameItemForm from './forms/GameItemForm';
import TextItemForm from './forms/TextItemForm';
import Column from '../../../core/layout/Column';
import VideoItemForm from './forms/VideoItemForm';
import TimeInput from '../../../core/form/TimeInput';
import PhonemeItemForm from './forms/PhonemeItemForm';
import GapFillItemForm from './forms/GapFillItemForm';
import Separator from '../../../core/layout/Separator';
import DialogueItemForm from './forms/DialogueItemForm';
import FreeTextItemForm from './forms/FreeTextItemForm';
import FormButtons from '../../../core/form/FormButtons';
import FetchSelect from '../../../core/form/FetchSelect';
import VideoLongItemForm from './forms/VideoLongItemForm';
import TrueFalseItemForm from './forms/TrueFalseItemForm';
import FreeSpeakItemForm from './forms/FreeSpeakItemForm';
import DictationItemForm from './forms/DictationItemForm';
import VideoShortItemForm from './forms/VideoShortItemForm';
import VocabularyItemForm from './forms/VocabularyItemForm';
import GrammarCheckItemForm from './forms/GrammarCheckItemForm';
import PresentationItemForm from './forms/PresentationItemForm';
import SingleChoiceItemForm from './forms/SingleChoiceItemForm';
import VideoLongTextItemForm from './forms/VideoLongTextItemForm';
import GapFillSelectItemForm from './forms/GapFillSelectItemForm';
import PronunciationItemForm from './forms/PronunciationItemForm';
import GapFillLetterItemForm from './forms/GapFillLetterItemForm';
import FreeTextImageItemForm from './forms/FreeTextImageItemForm';
import SpeechPracticeItemForm from './forms/SpeechPracticeItemForm';
import FreeSpeakImageItemForm from './forms/FreeSpeakImageItemForm';
import UnscrambleTextItemForm from './forms/UnscrambleTextItemForm';
import MultipleChoiceItemForm from './forms/MultipleChoiceItemForm';
import GapFillMultipleItemForm from './forms/GapFillMultipleItemForm';
import SingleChoiceGameItemForm from './forms/SingleChoiceGameItemForm';
import UnscrambleDragDropItemForm from './forms/UnscrambleDragDropItemForm';
import GapFillDictationItemForm from './forms/GapFillDictationItemForm';
import UnscrambleSpeechRecognitionItemForm from './forms/UnscrambleSpeechRecognitionItemForm';
import MeaningsAssociatingItemForm from './forms/MeaningsAssociatingItemForm';
import AudioLongItemForm from './forms/AudioLongItemForm';
import VideoUploadDialogContainer from '../../../core/form/VideoUploadDialogContainer';
import PreEssayItemForm from './forms/PreEssayItemForm';
import EssayItemForm from './forms/EssayItemForm';
import Button from '../../../core/form/Button';
import Switch from '../../../core/form/Switch';
import { some } from 'lodash';

const needCharacter = (itemType) =>
  [Roles.ADMIN, Roles.SUPPORT, Roles.CONTENT_ADMIN].some((r) => r === localStorage.role) &&
  !['VIDEO', 'VIDEO_SHORT', 'VIDEO_TEXT_AREA', 'TEXT'].find((type) => type === itemType);

const hasSavedItem = (props) => !!get(props.values.item, 'id');

const ItemForm = (props) => (
  <form
    onSubmit={(event) => {
      event.preventDefault();
      props.onSubmit();
    }}
  >
    <Async fetching={props.fetching}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', margin: '20px 0px' }}>
        {!props.isTestItem && (
          <>
            <Button
              icon="font-size"
              disabled={
                props.submitting ||
                props.disabled ||
                !hasSavedItem(props) ||
                (!get(props.values.item, 'text') && !get(props.values.item, 'postPhrase'))
              }
              onClick={props.onGenerateTranslation}
              label="Generate translations"
            />
            &emsp;
          </>
        )}
        <Button
          icon="form"
          disabled={
            props.submitting ||
            props.disabled ||
            !hasSavedItem(props) ||
            (!get(props.values.item, 'text') && !get(props.values.item, 'postPhrase'))
          }
          onClick={props.onGenerateImagePrompt}
          label="Generate image prompt"
        />
        &emsp;
        <Button
          icon="picture"
          disabled={
            props.submitting ||
            props.disabled ||
            !hasSavedItem(props) ||
            (!get(props.values.item, 'imagePrompt') &&
              !get(props.values.item, 'postPhraseImagePrompt') &&
              !some(get(props.values.item, 'answers', []), 'imagePrompt'))
          }
          onClick={props.onGenerateImage}
          label="Generate imagens"
        />
        &emsp;
        <Button
          icon="audio"
          disabled={
            props.submitting ||
            props.disabled ||
            !hasSavedItem(props) ||
            !get(props.values.item, 'character') ||
            (!get(props.values.item, 'text') && !get(props.values.item, 'postPhrase'))
          }
          onClick={props.onGenerateAudios}
          label="Generate audios"
        />
      </div>
      <Row>
        <Column size={4}>
          <FetchSelect
            required
            showSearch
            allowClear={false}
            url={props.itemsTypeUrl}
            disabled={props.submitting || props.disabled}
            label="Item Type"
            value={get(props.values, 'item.type.id', '')}
            onChange={(value, type) => {
              if (type) {
                props.onChange('item.type', type);
                if (type) {
                  props.onChange('item.time', type[props.timeProperty]);
                }
              }
            }}
            errorText={get(props.errors, 'item.type', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        <Column size={2}>
          <TimeInput
            required
            disabled={props.submitting || props.disabled || props.isTestItem}
            label="Time (minutes)"
            value={get(props.values, 'item.time', '')}
            onChange={(value) => props.onChange('item.time', value)}
            errorText={get(props.errors, 'item.time', '')}
          />
        </Column>
        <Column size={needCharacter(get(props.values.item, 'type.key')) ? 2 : 6}>
          <FetchSelect
            showSearch
            required
            url="grammars"
            disabled={props.submitting || props.disabled || !!props.defaultGrammar}
            label="Grammar"
            value={get(props.values, 'item.grammar.id', '')}
            onChange={(value, grammar) => props.onChange('item.grammar', grammar)}
            errorText={get(props.errors, 'item.grammar', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        {needCharacter(get(props.values.item, 'type.key')) && (
          <>
            <Column size={2}>
              <FetchSelect
                showSearch
                url="characters"
                apiVersion="v3"
                isPaginated
                disabled={props.submitting || props.disabled}
                label="Character"
                value={get(props.values, 'item.character', '')}
                onChange={(character) => {
                  props.onChange('item.character', character || null);
                }}
                errorText={get(props.errors, 'item.character', '')}
                resultTransformer={{
                  text: 'name',
                  value: 'id',
                }}
              />
            </Column>
            <Column size={2}>
              <Switch
                label="Character on image"
                disabled={props.submitting || props.disabled}
                value={get(props.values, 'item.useCharacterOnImage', true) !== false}
                onChange={(value) => props.onChange('item.useCharacterOnImage', value)}
              />
            </Column>
          </>
        )}
      </Row>
      {(get(props.values.item, 'type.key', '') === 'VIDEO' || get(props.values.item, 'type.key', '') === 'MUSIC') && (
        <VideoItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'VIDEO_SHORT' && (
        <VideoShortItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'VIDEO_LONG' && (
        <VideoLongItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'VIDEO_TEXT_AREA' && (
        <VideoLongTextItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {['SINGLE_CHOICE_TEXT', 'SINGLE_CHOICE_AUDIO', 'SINGLE_CHOICE_IMAGE', 'SINGLE_CHOICE_KIDS'].find(
        (type) => type === get(props.values.item, 'type.key')
      ) && (
        <SingleChoiceItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          type={get(props.values.item, 'type.key')}
          isTestItem={props.isTestItem}
          hasImage={['SINGLE_CHOICE_KIDS', 'SINGLE_CHOICE_IMAGE'].some(
            (type) => type === get(props.values.item, 'type.key')
          )}
        />
      )}
      {['MULTIPLE_CHOICE_TEXT'].find((type) => type === get(props.values.item, 'type.key')) && (
        <MultipleChoiceItemForm
          hasImage
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'DICTATION' && (
        <DictationItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {['GAP_FILL', 'GAP_FILL_IMAGE'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <GapFillItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
          hasImage={get(props.values.item, 'type.key', '') === 'GAP_FILL_IMAGE'}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'GAP_FILL_SELECT' && (
        <GapFillSelectItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'GAP_FILL_MULTIPLE' && (
        <GapFillMultipleItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'GAP_FILL_DICTATION' && (
        <GapFillDictationItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'PRESENTATION' && (
        <PresentationItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
          showPostPhrase={props.showPostPhrase}
          onRefreshNativeSpeechRecognition={props.onRefreshNativeSpeechRecognition}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'PRONUNCIATION' && (
        <PronunciationItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
          onRefreshNativeSpeechRecognition={props.onRefreshNativeSpeechRecognition}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'SPEECH_PRACTICE' && (
        <SpeechPracticeItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
          onRefreshNativeSpeechRecognition={props.onRefreshNativeSpeechRecognition}
        />
      )}
      {['TEXT'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <TextItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'AUDIO_LONG' && (
        <AudioLongItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'UNSCRAMBLE_DRAG_AND_DROP' && (
        <UnscrambleDragDropItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'UNSCRAMBLE_SPEECH_RECOGNITION' && (
        <UnscrambleSpeechRecognitionItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {['TRUE_FALSE', 'TRUE_FALSE_KIDS'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <TrueFalseItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
          hasImage={get(props.values.item, 'type.key', '') === 'TRUE_FALSE_KIDS'}
          hasPostPhrase={get(props.values.item, 'type.key', '') === 'TRUE_FALSE_KIDS'}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'FREE_TEXT' && (
        <FreeTextItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'FREE_TEXT_IMAGE' && (
        <FreeTextImageItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'FREE_SPEAK' && (
        <FreeSpeakItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {['FREE_SPEAK_IMAGE', 'IMAGE'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <FreeSpeakImageItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {['CONNECTING_DOTS', 'MEMORY_GAME', 'VOCABULARY_GAME'].find(
        (type) => type === get(props.values.item, 'type.key', '')
      ) && (
        <GameItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          hasPostPhraseImage={get(props.values.item, 'type.key', '') === 'VOCABULARY_GAME'}
        />
      )}
      {['VOCABULARY', 'VOCABULARY_ACADEMIC'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <VocabularyItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          hasPostPhraseImage={get(props.values.item, 'type.key', '') === 'VOCABULARY'}
          onRefreshNativeSpeechRecognition={props.onRefreshNativeSpeechRecognition}
        />
      )}
      {['PHONEME'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <PhonemeItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'GAP_FILL_LETTER' && (
        <GapFillLetterItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          isTestItem={props.isTestItem}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'SINGLE_CHOICE_GAME' && (
        <SingleChoiceGameItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'UNSCRAMBLE_TEXT' && (
        <UnscrambleTextItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {['DIALOGUE', 'DIALOGUE_OPTION'].find((type) => type === get(props.values.item, 'type.key', '')) && (
        <DialogueItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
          submitting={props.submitting}
          disabled={props.disabled}
          type={get(props.values.item, 'type.key')}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'GRAMMAR_CHECK' && (
        <GrammarCheckItemForm
          onChange={(path, value) => props.onChange(`item.${path}`, value)}
          errors={get(props.errors, 'item', {})}
          values={props.values.item}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'MEANINGS_ASSOCIATING' && (
        <MeaningsAssociatingItemForm
          values={props.values.item}
          onChange={(path, value) => {
            props.onChange(`item.${path}`, value);
          }}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'PRE_ESSAY' && (
        <PreEssayItemForm
          values={props.values.item}
          onChange={(path, value) => {
            props.onChange(`item.${path}`, value);
          }}
          errors={get(props.errors, 'item', {})}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      {get(props.values.item, 'type.key', '') === 'ESSAY' && (
        <EssayItemForm
          values={props.values.item}
          onChange={(path, value) => {
            props.onChange(`item.${path}`, value);
          }}
          errors={get(props.errors, 'item', {})}
          submitting={props.submitting}
          disabled={props.disabled}
        />
      )}
      <Separator size="xs" />
      {!props.disabled && (
        <FormButtons
          confirmLabel={props.values.id || get(props.values, 'item.id', '') ? 'Update Item' : 'Create Item'}
          isDisabled={props.submitting || !props.isDirty()}
          isSubmitting={props.submitting}
          onReset={props.onReset}
        />
      )}
    </Async>
    {['VIDEO', 'VIDEO_LONG', 'VIDEO_SHORT', 'MUSIC'].includes(get(props.values.item, 'type.key', '')) && (
      <VideoUploadDialogContainer />
    )}
  </form>
);

ItemForm.propTypes = {
  onSubmit: PropTypes.func,
  onRefreshNativeSpeechRecognition: PropTypes.func,
  onGenerateTranslation: PropTypes.func,
  onGenerateImage: PropTypes.func,
  onGenerateAudios: PropTypes.func,
  onReset: PropTypes.func,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  fetching: PropTypes.bool,
  isDirty: PropTypes.func,
  itemsTypeUrl: PropTypes.string.isRequired,
  timeProperty: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isTestItem: PropTypes.bool,
};

ItemForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  fetching: false,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onRefreshNativeSpeechRecognition: () => alert('onRefreshNativeSpeechRecognition'),
  onReset: () => false,
  disabled: false,
  isTestItem: false,
};

export default ItemForm;
